import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'

/**
 * Auto-pulled figures a sourcer may override with local knowledge.
 * `key` is the dot-path the backend expects in manual_overrides[].field_name.
 */
const OVERRIDE_FIELDS = [
  { key: 'financials.monthly_rent',       label: 'Monthly rent',                prefix: '£', numeric: true },
  { key: 'chart_config.renovation_cost',  label: 'Refurb / renovation estimate', prefix: '£', numeric: true },
  { key: 'features.sqft',                 label: 'Floor area (sq ft)',          prefix: '',  numeric: true },
  { key: 'features.epc_rating',           label: 'EPC rating',                  prefix: '',  numeric: false },
]

/**
 * Normalise a FastAPI `detail` into a display string.
 * Handles string detail, {code,message}/{msg} objects, and validation arrays.
 */
function formatDetail(detail) {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
  if (typeof detail === 'object') return detail.message || detail.msg || JSON.stringify(detail)
  return String(detail)
}

/** Controlled colour swatch: native colour picker + hex text display side by side. */
function ColourInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-gray-500 w-28 shrink-0">{label}</label>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-7 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
        aria-label={label}
      />
      <span className="text-xs font-mono text-gray-600">{value}</span>
    </div>
  )
}

/** Single-field row for the compact summary dl. */
function DlRow({ label, value }) {
  if (value == null || value === '') return null
  return (
    <div className="flex justify-between border-b border-gray-100 pb-1 last:border-0">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-800 text-right max-w-[60%] break-words">{String(value)}</dd>
    </div>
  )
}

/** Section heading with a subtle rule. */
function SectionHead({ children }) {
  return (
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-5 mb-2 border-b border-gray-100 pb-1">
      {children}
    </h3>
  )
}

export default function DealPackTab({ result }) {
  const API_BASE = import.meta.env.VITE_API_URL
    ? `${String(import.meta.env.VITE_API_URL).replace(/\/$/, '')}/api/v1`
    : '/api/v1'

  // ── Deal inputs ────────────────────────────────────────────────────────────
  const [agreedPrice, setAgreedPrice] = useState('')
  const [marketValue, setMarketValue] = useState('')
  const [sourcingFee, setSourcingFee] = useState('')
  const [sourcingFeeStructure, setSourcingFeeStructure] = useState('fixed fee')
  const [dealTerms, setDealTerms] = useState('')
  const [investorProfile, setInvestorProfile] = useState('')
  const [photosRaw, setPhotosRaw] = useState('')   // one URL per line
  const [keyRisks, setKeyRisks] = useState('')
  const [sourcerNotes, setSourcerNotes] = useState('')

  // ── Manual overrides of auto-pulled figures ─────────────────────────────────
  // overrides: { [field_name]: { value: string, reason: string } }
  const [overrides, setOverrides] = useState({})

  // Auto-pulled baseline values (shown as the figure being overridden).
  const pulled = useMemo(() => {
    const snap = result?.property_snapshot
    const strategies = result?.strategy_analysis?.analyzed_strategies || []
    const proposed = strategies.find(
      s => s.strategy === result?.strategy_analysis?.recommended_strategy
    ) || strategies[0]
    return {
      'financials.monthly_rent': snap?.financials?.monthly_rent ?? null,
      'chart_config.renovation_cost': proposed?.chart_config?.renovation_cost ?? null,
      'features.sqft': snap?.features?.sqft ?? null,
      'features.epc_rating': snap?.features?.epc_rating ?? null,
    }
  }, [result?.id])

  const setOverride = useCallback((key, patch) => {
    setOverrides(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  }, [])

  // ── Brand config ────────────────────────────────────────────────────────────
  const [companyName, setCompanyName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [primaryColour, setPrimaryColour] = useState('#1a1a2e')
  const [accentColour, setAccentColour] = useState('#c9a84c')
  const [regulatoryMembership, setRegulatoryMembership] = useState('')
  const [companyNumber, setCompanyNumber] = useState('')
  const [piInsurance, setPiInsurance] = useState('')
  const [disclaimerOverride, setDisclaimerOverride] = useState('')

  // ── UI state ────────────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState(null)  // null | 'saving' | 'saved' | 'error'
  const [savedBmv, setSavedBmv] = useState(null)
  const [savedAt, setSavedAt] = useState(null)
  const [validationErrors, setValidationErrors] = useState([])

  // ── Pack generation / download state ─────────────────────────────────────────
  const [packId, setPackId] = useState(null)
  const [packHtml, setPackHtml] = useState(null)  // generated HTML for in-app preview
  const [previewOpen, setPreviewOpen] = useState(false)
  const [generateStatus, setGenerateStatus] = useState(null)  // null | 'generating' | 'ready' | 'error'
  const [packError, setPackError] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [dataApiCalls, setDataApiCalls] = useState(null)  // internal PropertyData cost signal

  // PDF export is blocked by the backend if the analysis failed data-quality gates.
  const pdfBlocked = result?.pdf_export_blocked === true

  // Pre-populate from analysis result on mount / result change
  useEffect(() => {
    if (!result) return
    const snap = result.property_snapshot
    const asking = snap?.financials?.asking_price
    if (asking && !agreedPrice) setAgreedPrice(String(asking))
    const estVal = result.valuation?.estimated_value
    if (estVal && !marketValue) setMarketValue(String(Math.round(estVal)))
  }, [result?.id])

  // Compute BMV live from form state
  const bmvPercent = (() => {
    const ap = parseFloat(agreedPrice)
    const mv = parseFloat(marketValue)
    if (ap > 0 && mv > 0) return ((1 - ap / mv) * 100).toFixed(1)
    return null
  })()

  const validate = useCallback(() => {
    const errors = []
    if (!agreedPrice || parseFloat(agreedPrice) <= 0) errors.push('Agreed price must be a positive number.')
    if (marketValue && parseFloat(marketValue) <= 0) errors.push('Market value must be a positive number if provided.')
    if (!sourcingFee && sourcingFee !== '0') errors.push('Sourcing fee is required (enter 0 if not charged).')
    if (!companyName.trim()) errors.push("Company name is required in Brand Config.")
    return errors
  }, [agreedPrice, marketValue, sourcingFee, companyName])

  const handleSave = useCallback(async () => {
    const errors = validate()
    setValidationErrors(errors)
    if (errors.length > 0) return

    setSaveStatus('saving')
    setSavedBmv(null)
    // Invalidate any previously-generated pack so a stale PDF isn't downloaded after edits.
    setPackId(null)
    setPackHtml(null)
    setPreviewOpen(false)
    setGenerateStatus(null)
    setPackError(null)
    setDataApiCalls(null)

    const photos = photosRaw
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 10)

    // Build manual_overrides from any field the operator actually changed.
    const manualOverrides = OVERRIDE_FIELDS.flatMap(f => {
      const o = overrides[f.key]
      const raw = o?.value
      if (raw == null || String(raw).trim() === '') return []
      const override_value = f.numeric ? parseFloat(raw) : String(raw).trim()
      if (f.numeric && Number.isNaN(override_value)) return []
      return [{
        field_name: f.key,
        original_value: pulled[f.key],
        override_value,
        override_reason: (o.reason || '').trim() || null,
      }]
    })

    const payload = {
      analysis_id: result.id,
      deal_inputs: {
        agreed_price: parseFloat(agreedPrice),
        market_value: marketValue ? parseFloat(marketValue) : null,
        sourcing_fee: parseFloat(sourcingFee || '0'),
        sourcing_fee_structure: sourcingFeeStructure || 'fixed fee',
        deal_terms: dealTerms || null,
        investor_profile: investorProfile || null,
        property_photos: photos,
        key_risks: keyRisks || null,
        sourcer_notes: sourcerNotes || null,
        manual_overrides: manualOverrides,
      },
      pack_config: {
        company_name: companyName.trim(),
        contact_name: contactName || null,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
        website: website || null,
        logo_url: logoUrl || null,
        primary_colour: primaryColour,
        accent_colour: accentColour,
        regulatory_membership: regulatoryMembership || null,
        company_number: companyNumber || null,
        pi_insurance: piInsurance || null,
        disclaimer_override: disclaimerOverride || null,
      },
    }

    try {
      const { data } = await axios.post(`${API_BASE}/pack/save`, payload)
      setSavedBmv(data.bmv_percent)
      setSavedAt(new Date(data.saved_at).toLocaleTimeString())
      setSaveStatus('saved')
      setValidationErrors([])
    } catch (err) {
      console.error('Pack save failed:', err)
      setSaveStatus('error')
    }
  }, [
    result, agreedPrice, marketValue, sourcingFee, sourcingFeeStructure,
    dealTerms, investorProfile, photosRaw, keyRisks, sourcerNotes,
    companyName, contactName, contactEmail, contactPhone, website,
    logoUrl, primaryColour, accentColour, regulatoryMembership,
    companyNumber, piInsurance, disclaimerOverride, validate, API_BASE,
    overrides, pulled,
  ])

  // Generate the investor pack HTML on the server (requires a prior successful save).
  const handleGenerate = useCallback(async () => {
    setGenerateStatus('generating')
    setPackError(null)
    try {
      const { data } = await axios.post(`${API_BASE}/pack/generate`, { analysis_id: result.id })
      setPackId(data.pack_id)
      setPackHtml(data.html || null)
      setDataApiCalls(typeof data.data_api_calls === 'number' ? data.data_api_calls : null)
      setGenerateStatus('ready')
    } catch (err) {
      console.error('Pack generate failed:', err)
      setPackError(formatDetail(err.response?.data?.detail) || err.message || 'Generation failed')
      setGenerateStatus('error')
    }
  }, [API_BASE, result])

  // Open the in-app preview panel (review the exact pack before downloading).
  const handlePreview = useCallback(() => {
    if (!packHtml) return
    setPreviewOpen(true)
  }, [packHtml])

  // Open the generated pack as a standalone HTML page in a new tab.
  const handleOpenInNewTab = useCallback(() => {
    if (!packId) return
    window.open(`${API_BASE}/pack/download/${packId}`, '_blank', 'noopener')
  }, [API_BASE, packId])

  // Download the generated pack as a PDF (blob). Error bodies arrive as Blob with
  // responseType:'blob', so they must be read back and JSON-parsed to recover the detail.
  const handleDownloadPdf = useCallback(async () => {
    if (!packId || pdfBlocked) return
    setPdfLoading(true)
    try {
      const res = await axios.get(`${API_BASE}/pack/pdf/${packId}`, { responseType: 'blob' })
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `InvestorPack_${result.id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      let msg = err.message
      if (err.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text())
          msg = formatDetail(parsed?.detail) || msg
        } catch { /* keep msg */ }
      }
      alert(`PDF download failed: ${msg}`)
    } finally {
      setPdfLoading(false)
    }
  }, [API_BASE, packId, pdfBlocked, result])

  if (!result) return null

  return (
    <div className="flex-1 overflow-y-auto bg-white rounded-lg border border-gray-200 text-sm">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Investor Pack Builder</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Fill in deal details and your brand config, save, then generate a branded investor pack
          to preview as HTML or download as PDF.
        </p>
      </div>

      <div className="px-5 pb-6">

        {/* ── SECTION 1: DEAL DETAILS ────────────────────────────────────── */}
        <SectionHead>1 — Deal Details</SectionHead>

        {/* Pricing */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Agreed Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">£</span>
              <input
                type="number"
                min="0"
                step="1000"
                value={agreedPrice}
                onChange={e => setAgreedPrice(e.target.value)}
                placeholder="285000"
                className="w-full border border-gray-300 rounded-md pl-5 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Market Value</label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">£</span>
              <input
                type="number"
                min="0"
                step="1000"
                value={marketValue}
                onChange={e => setMarketValue(e.target.value)}
                placeholder="325000"
                className="w-full border border-gray-300 rounded-md pl-5 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
          </div>
        </div>

        {/* Live BMV badge */}
        {bmvPercent !== null && (
          <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
            parseFloat(bmvPercent) > 0
              ? 'bg-green-100 text-green-800'
              : parseFloat(bmvPercent) < 0
              ? 'bg-red-100 text-red-800'
              : 'bg-gray-100 text-gray-600'
          }`}>
            <span>{parseFloat(bmvPercent) > 0 ? '↓' : parseFloat(bmvPercent) < 0 ? '↑' : '='}</span>
            {parseFloat(bmvPercent) > 0
              ? `${bmvPercent}% below market value`
              : parseFloat(bmvPercent) < 0
              ? `${Math.abs(parseFloat(bmvPercent))}% above market value`
              : 'At market value'}
          </div>
        )}

        {/* Sourcing */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Sourcing Fee <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">£</span>
              <input
                type="number"
                min="0"
                step="500"
                value={sourcingFee}
                onChange={e => setSourcingFee(e.target.value)}
                placeholder="4500"
                className="w-full border border-gray-300 rounded-md pl-5 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Fee Structure</label>
            <input
              type="text"
              value={sourcingFeeStructure}
              onChange={e => setSourcingFeeStructure(e.target.value)}
              placeholder="fixed fee"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">Deal Terms / Structure</label>
          <textarea
            rows={2}
            value={dealTerms}
            onChange={e => setDealTerms(e.target.value)}
            placeholder="e.g. Purchase option, 28-day completion, no chain"
            className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        {/* Investor profile + notes */}
        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">Suitable Investor Profile</label>
          <textarea
            rows={2}
            value={investorProfile}
            onChange={e => setInvestorProfile(e.target.value)}
            placeholder="e.g. Cash or mortgage buyer, 6%+ gross yield target, 5–10yr hold, no active management"
            className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">
            Property Photos
            <span className="text-gray-400 font-normal ml-1">(one URL per line, cover photo first, max 10)</span>
          </label>
          <textarea
            rows={3}
            value={photosRaw}
            onChange={e => setPhotosRaw(e.target.value)}
            placeholder={"https://example.com/photo1.jpg\nhttps://example.com/photo2.jpg"}
            className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs font-mono resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
          {photosRaw && (
            <p className="text-xs text-gray-400 mt-0.5">
              {photosRaw.split('\n').filter(s => s.trim()).length} photo(s) entered
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Key Risks</label>
            <textarea
              rows={3}
              value={keyRisks}
              onChange={e => setKeyRisks(e.target.value)}
              placeholder="e.g. Article 4 direction in force — check PD rights before HMO conversion"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sourcer Notes</label>
            <textarea
              rows={3}
              value={sourcerNotes}
              onChange={e => setSourcerNotes(e.target.value)}
              placeholder="e.g. Off-market deal, vendor motivated by probate — realistic on price"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* ── SECTION 1b: OVERRIDE AUTO-PULLED FIGURES ──────────────────── */}
        <SectionHead>Override auto-pulled figures</SectionHead>
        <p className="text-xs text-gray-400 mb-2">
          Auto-pulled data is a starting point. Override any figure with your local knowledge —
          overridden figures are flagged in the pack's Assumptions &amp; Sources section.
        </p>
        <div className="space-y-2">
          {OVERRIDE_FIELDS.map(f => {
            const base = pulled[f.key]
            const hasBaseline = base !== null && base !== undefined && base !== ''
            const baseDisplay = hasBaseline
              ? (f.numeric ? `${f.prefix}${Number(base).toLocaleString()}` : String(base))
              : 'not available'
            const o = overrides[f.key] || {}
            return (
              <div key={f.key} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <div className="text-xs text-gray-600 font-medium">{f.label}</div>
                  <div className="text-xs text-gray-400">Auto-pulled: {baseDisplay}</div>
                </div>
                <div className="col-span-3 relative">
                  {f.prefix && o.value && (
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{f.prefix}</span>
                  )}
                  <input
                    type={f.numeric ? 'number' : 'text'}
                    value={o.value || ''}
                    onChange={e => setOverride(f.key, { value: e.target.value })}
                    placeholder="Override"
                    className={`w-full border border-gray-300 rounded-md ${f.prefix && o.value ? 'pl-5' : 'pl-2.5'} pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400`}
                  />
                </div>
                <div className="col-span-5">
                  <input
                    type="text"
                    value={o.reason || ''}
                    onChange={e => setOverride(f.key, { reason: e.target.value })}
                    placeholder="Reason (shown in pack)"
                    disabled={!o.value}
                    className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* ── SECTION 2: BRAND CONFIG ───────────────────────────────────── */}
        <SectionHead>2 — Brand Config</SectionHead>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="Apex Property Sourcing Ltd"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Contact Name</label>
            <input
              type="text"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              placeholder="James Hargreaves"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Phone</label>
            <input
              type="text"
              value={contactPhone}
              onChange={e => setContactPhone(e.target.value)}
              placeholder="+44 7700 900000"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Email</label>
            <input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="james@apexsourcing.co.uk"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Website</label>
            <input
              type="text"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              placeholder="https://apexsourcing.co.uk"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs text-gray-500 mb-1">Logo URL</label>
          <input
            type="text"
            value={logoUrl}
            onChange={e => setLogoUrl(e.target.value)}
            placeholder="https://apexsourcing.co.uk/logo.png"
            className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div className="mt-3 space-y-2">
          <ColourInput label="Primary colour" value={primaryColour} onChange={setPrimaryColour} />
          <ColourInput label="Accent colour" value={accentColour} onChange={setAccentColour} />
        </div>

        {/* Regulatory */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Regulatory Membership</label>
            <input
              type="text"
              value={regulatoryMembership}
              onChange={e => setRegulatoryMembership(e.target.value)}
              placeholder="Property Redress Scheme member: PRS12345"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Companies House No.</label>
            <input
              type="text"
              value={companyNumber}
              onChange={e => setCompanyNumber(e.target.value)}
              placeholder="12345678"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">PI Insurance</label>
            <input
              type="text"
              value={piInsurance}
              onChange={e => setPiInsurance(e.target.value)}
              placeholder="Professional Indemnity Insurance held"
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">
              Custom Disclaimer
              <span className="text-gray-400 font-normal ml-1">(leave blank to use default)</span>
            </label>
            <textarea
              rows={2}
              value={disclaimerOverride}
              onChange={e => setDisclaimerOverride(e.target.value)}
              placeholder="Leave blank — the standard DealPack disclaimer will be used."
              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* ── Validation errors ────────────────────────────────────────── */}
        {validationErrors.length > 0 && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
            <p className="text-xs font-semibold text-red-700 mb-1">Please fix the following before saving:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              {validationErrors.map((e, i) => (
                <li key={i} className="text-xs text-red-600">{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Save button + status ─────────────────────────────────────── */}
        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-2"
          >
            {saveStatus === 'saving' ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                Saving…
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Pack Data
              </>
            )}
          </button>

          {saveStatus === 'saved' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Saved at {savedAt}
              </span>
              {savedBmv !== null && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  savedBmv > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {savedBmv > 0 ? `${savedBmv}% BMV` : 'At / above market'}
                </span>
              )}
            </div>
          )}

          {saveStatus === 'error' && (
            <span className="text-xs text-red-600 font-medium">
              Save failed — check console for details.
            </span>
          )}
        </div>

        {/* ── Summary preview (shown after save) ──────────────────────── */}
        {saveStatus === 'saved' && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">Pack summary</p>
            <dl className="space-y-1 text-xs">
              <DlRow label="Agreed price" value={agreedPrice ? `£${Number(agreedPrice).toLocaleString()}` : null} />
              {marketValue && <DlRow label="Market value" value={`£${Number(marketValue).toLocaleString()}`} />}
              {savedBmv !== null && <DlRow label="BMV" value={savedBmv > 0 ? `${savedBmv}% below market` : 'At / above market'} />}
              <DlRow label="Sourcing fee" value={sourcingFee ? `£${Number(sourcingFee).toLocaleString()} (${sourcingFeeStructure})` : null} />
              <DlRow label="Company" value={companyName} />
              {contactEmail && <DlRow label="Contact" value={contactEmail} />}
              {regulatoryMembership && <DlRow label="Regulatory" value={regulatoryMembership} />}
            </dl>

            {/* ── Generate / download controls ─────────────────────────── */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {pdfBlocked && (
                <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-2.5 flex items-start gap-2">
                  <span className="text-amber-600" aria-hidden>⚠️</span>
                  <p className="text-xs text-amber-700">
                    PDF download is disabled for this analysis because it is flagged for review
                    or failed a data-quality gate. You can still preview the pack as HTML.
                  </p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {/* Generate */}
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generateStatus === 'generating'}
                  className="px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-60 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-2"
                >
                  {generateStatus === 'generating' ? (
                    <>
                      <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                      Generating…
                    </>
                  ) : generateStatus === 'ready' ? 'Regenerate Pack' : 'Generate Pack'}
                </button>

                {/* Preview + Download — revealed once a pack is ready */}
                {generateStatus === 'ready' && packId && (
                  <>
                    <button
                      type="button"
                      onClick={handlePreview}
                      className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview HTML
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      disabled={pdfLoading || pdfBlocked}
                      title={pdfBlocked ? 'PDF export is blocked for this analysis' : undefined}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                    >
                      {pdfLoading ? (
                        <>
                          <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                          Generating PDF…
                        </>
                      ) : (
                        <>
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download PDF
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>

              {generateStatus === 'ready' && (
                <div className="mt-2">
                  <p className="text-xs text-green-700 font-medium flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Pack ready — preview it or download the PDF.
                  </p>
                  {dataApiCalls !== null && (
                    <p className="text-xs text-gray-400 mt-1" title="Internal cost signal — not shown in the investor pack">
                      Internal: ~{dataApiCalls} PropertyData API request{dataApiCalls === 1 ? '' : 's'} for this postcode this session.
                    </p>
                  )}
                </div>
              )}

              {generateStatus === 'error' && (
                <p className="text-xs text-red-600 font-medium mt-2">
                  {packError || 'Pack generation failed — check console for details.'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── In-app investor-pack preview (review before download) ──────────── */}
      {previewOpen && packHtml && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Investor pack preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal toolbar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Investor Pack Preview</h3>
                <p className="text-xs text-gray-400">This is exactly what the investor receives — review before download.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  className="px-3 py-1.5 border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                >
                  Open in new tab
                </button>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading || pdfBlocked}
                  title={pdfBlocked ? 'PDF export is blocked for this analysis' : undefined}
                  className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                >
                  {pdfLoading ? 'Generating PDF…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Close preview"
                  className="px-2 py-1.5 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Sandboxed pack render */}
            <iframe
              title="Investor pack preview"
              srcDoc={packHtml}
              sandbox="allow-same-origin"
              className="flex-1 w-full border-0 bg-gray-100"
            />
          </div>
        </div>
      )}
    </div>
  )
}
